import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import { FieldArray } from 'formik';
import { Collapse } from '@mui/material';
import { $invoices } from '@features/enterprises-tickets/models/ticket-invoices';
import { $types } from '@features/tickets/models/page';
import { $marketOrders } from '@features/tickets/models/ticket-invoice';
import { Ticket } from '../../interfaces';
import { $mode, $ticket } from '../../models';
import { SectionHeader, TicketInvoiceItem, TicketMarketOrders } from '../../molecules';
import { useStyles } from '../detail/components/styles';
import * as Styled from './styles';

interface Props {
  values: Ticket;
  setFieldValue: (name: string, value: any) => void;
  errors: { invoices: { [key: number]: string }[] };
  isNew: boolean;
}

export const TicketInvoice: FC<Props> = ({ values, setFieldValue, errors, isNew }) => {
  const { t } = useTranslation();
  const [ticket, mode, invoicesSource, marketOrdersSource, types] = useUnit([
    $ticket,
    $mode,
    $invoices,
    $marketOrders,
    $types,
  ]);
  const [expanded, setExpanded] = useState(true);
  const classes = useStyles({ external: false });

  const { invoices, marketOrders } = ticket as Ticket;
  const isPaidtype =
    values.types.length > 0 ? Boolean([...values.types].reverse()[0]?.paid) : false;

  const handleExpanded = () => setExpanded(!expanded);

  useEffect(() => {
    if (invoices.length > 0 && values.invoices.length > 0) {
      if (JSON.stringify(invoices) !== JSON.stringify(values.invoices)) {
        setFieldValue('invoices', invoices);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, types]);

  useEffect(() => {
    if (marketOrders.length > 0 && values.marketOrders.length > 0) {
      if (JSON.stringify(marketOrders) !== JSON.stringify(values.marketOrders)) {
        setFieldValue('marketOrders', marketOrders);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketOrders, types]);

  const notShowThisBlock = !isPaidtype && !values?.marketOrders?.length;

  if (notShowThisBlock || isNew) {
    return null;
  }

  return (
    <div
      className={classes.container}
      key={JSON.stringify({ invoicesSource, marketOrdersSource, types })}
    >
      <SectionHeader
        title={t('Invoices')}
        isExpanded={expanded}
        onClick={handleExpanded}
      />
      <Collapse in={expanded} timeout="auto">
        {values.marketOrders.length > 0 && (
          <FieldArray
            name="marketOrders"
            render={({ push, remove }) => (
              <TicketMarketOrders
                push={push}
                remove={remove}
                values={values}
                setFieldValue={setFieldValue}
                errors={errors}
              />
            )}
          />
        )}

        {(values.invoices.length > 0 || mode === 'edit') && (
          <FieldArray
            name="invoices"
            render={({ push, remove }) => (
              <TicketInvoiceItem
                push={push}
                remove={remove}
                values={values}
                setFieldValue={setFieldValue}
                errors={errors}
              />
            )}
          />
        )}
        {values.invoices.length === 0 && mode === 'view' && (
          <Styled.NotInvoicesText>{t('NoInvoicesAdded')}</Styled.NotInvoicesText>
        )}
      </Collapse>
    </div>
  );
};
